import tipotLogo from 'assets/images/tipot-logo.png';
import { ReactComponent as KakaoLogo } from 'assets/images/kakao.login.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import TermsModal from 'components/common/TermsModal';
import PrivacyModal from 'components/common/PrivacyModal';

const LoginComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isSignupMode = location.pathname.startsWith('/signup');

    const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);

    // Agreement state (signup only)
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [ageAgreed, setAgeAgreed] = useState(false);

    const allAgreed = termsAgreed && privacyAgreed && ageAgreed;

    const handleAllAgree = () => {
        const newVal = !allAgreed;
        setTermsAgreed(newVal);
        setPrivacyAgreed(newVal);
        setAgeAgreed(newVal);
    };

    const handleKakaoLogin = () => {
        if (isSignupMode && !allAgreed) return;
        window.open(`${process.env.REACT_APP_API_URL}/auth/user/login/kakao`, '_self');
    };

    const canProceed = isSignupMode ? allAgreed : true;

    return (
        <div className="min-h-screen flex items-center justify-center bg-white px-5">
            <div className="w-full max-w-[380px] animate-fadeIn">
                {/* Logo */}
                <div className="flex flex-col items-center mb-10">
                    <img
                        src={tipotLogo}
                        alt="Tipot"
                        className="w-[90px] mb-4"
                        style={{ animation: 'fadeScale 0.6s ease-out' }}
                    />
                    <h1
                        style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, letterSpacing: '-0.5px' }}
                        className="text-[28px] text-gray-900 mb-2"
                    >
                        Ti<span className="text-indigo-500">Pot</span>
                    </h1>
                    <p className="text-[14px] text-achromatic-400 tracking-tight">
                        {isSignupMode ? '시간을 사고팔고, 새로운 경험을 시작하세요' : '당신의 시간, 가치있게 거래하세요'}
                    </p>
                </div>

                {/* Feature highlights */}
                <div className="flex justify-center gap-8 mb-8">
                    {[
                        { emoji: '💬', label: '수다·티타임' },
                        { emoji: '🎮', label: '게임·듀오' },
                        { emoji: '🔮', label: '타로·운세' },
                    ].map((item) => (
                        <div key={item.label} className="flex flex-col items-center gap-1.5">
                            <div className="w-12 h-12 bg-achromatic-50 rounded-2xl flex items-center justify-center text-xl">
                                {item.emoji}
                            </div>
                            <span className="text-[11px] text-achromatic-500 font-medium">{item.label}</span>
                        </div>
                    ))}
                </div>

                {/* Agreement Checkboxes (signup mode only) */}
                {isSignupMode && (
                    <div className="bg-achromatic-50/50 rounded-2xl p-5 mb-5">
                        {/* All agree */}
                        <label className="flex items-center gap-3 mb-3.5 cursor-pointer group">
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0
                                ${allAgreed ? 'bg-indigo-500 border-indigo-500' : 'border-achromatic-300 group-hover:border-achromatic-400'}`}>
                                {allAgreed && <span className="text-white text-[10px] font-bold">✓</span>}
                            </div>
                            <span className="text-[13px] font-bold text-achromatic-800">전체 동의하기</span>
                            <input type="checkbox" checked={allAgreed} onChange={handleAllAgree} className="hidden" />
                        </label>

                        <div className="border-t border-achromatic-100 pt-3 flex flex-col gap-2.5">
                            {/* Terms */}
                            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setTermsAgreed(!termsAgreed)}>
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0
                                    ${termsAgreed ? 'bg-indigo-500 border-indigo-500' : 'border-achromatic-300 group-hover:border-achromatic-400'}`}>
                                    {termsAgreed && <span className="text-white text-[8px] font-bold">✓</span>}
                                </div>
                                <span className="text-[12px] text-achromatic-500 flex-1">
                                    <span className="text-indigo-500 font-semibold">[필수]</span>{" "}
                                    <button onClick={(e) => { e.stopPropagation(); setShowTerms(true); }}
                                        className="underline underline-offset-2 hover:text-achromatic-700">이용약관</button>에 동의합니다
                                </span>
                            </div>

                            {/* Privacy */}
                            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setPrivacyAgreed(!privacyAgreed)}>
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0
                                    ${privacyAgreed ? 'bg-indigo-500 border-indigo-500' : 'border-achromatic-300 group-hover:border-achromatic-400'}`}>
                                    {privacyAgreed && <span className="text-white text-[8px] font-bold">✓</span>}
                                </div>
                                <span className="text-[12px] text-achromatic-500 flex-1">
                                    <span className="text-indigo-500 font-semibold">[필수]</span>{" "}
                                    <button onClick={(e) => { e.stopPropagation(); setShowPrivacy(true); }}
                                        className="underline underline-offset-2 hover:text-achromatic-700">개인정보 처리방침</button>에 동의합니다
                                </span>
                            </div>

                            {/* Age */}
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0
                                    ${ageAgreed ? 'bg-indigo-500 border-indigo-500' : 'border-achromatic-300 group-hover:border-achromatic-400'}`}>
                                    {ageAgreed && <span className="text-white text-[8px] font-bold">✓</span>}
                                </div>
                                <span className="text-[12px] text-achromatic-500 flex-1">
                                    <span className="text-indigo-500 font-semibold">[필수]</span> 만 14세 이상입니다
                                </span>
                                <input type="checkbox" checked={ageAgreed} onChange={() => setAgeAgreed(!ageAgreed)} className="hidden" />
                            </label>
                        </div>
                    </div>
                )}

                {/* Kakao Login Button */}
                <button
                    className={`flex items-center justify-center gap-3 w-full h-[54px] rounded-2xl text-[15px] font-bold transition-all mb-4
                        ${canProceed
                            ? 'bg-[#FEE500] text-[#3C1E1E] hover:brightness-[0.97] active:scale-[0.98] shadow-sm'
                            : 'bg-achromatic-100 text-achromatic-400 cursor-not-allowed'}`}
                    onClick={handleKakaoLogin}
                    disabled={!canProceed}
                >
                    <KakaoLogo className="w-5 h-5" />
                    {isSignupMode ? '카카오로 3초만에 시작하기' : '카카오로 로그인'}
                </button>

                {/* Mode switch + Guest link */}
                <div className="text-center mb-2">
                    {isSignupMode ? (
                        <span className="text-[13px] text-achromatic-400">
                            이미 계정이 있나요?{' '}
                            <button onClick={() => navigate('/login')} className="text-indigo-500 font-semibold hover:text-indigo-600 transition-colors">
                                로그인
                            </button>
                        </span>
                    ) : (
                        <span className="text-[13px] text-achromatic-400">
                            처음이신가요?{' '}
                            <button onClick={() => navigate('/signup')} className="text-indigo-500 font-semibold hover:text-indigo-600 transition-colors">
                                회원가입
                            </button>
                        </span>
                    )}
                </div>

                <button
                    className="w-full h-[44px] rounded-2xl text-[13px] text-achromatic-400 hover:text-achromatic-600 hover:bg-achromatic-50 transition-all font-medium"
                    onClick={() => navigate('/explore')}
                >
                    먼저 둘러보기 →
                </button>

                {/* Terms note (login mode) */}
                {!isSignupMode && (
                    <p className="text-[11px] text-center text-achromatic-300 leading-relaxed mt-4">
                        로그인 시{' '}
                        <button onClick={() => setShowTerms(true)} className="text-achromatic-400 underline underline-offset-2 hover:text-achromatic-600">이용약관</button>
                        {' '}및{' '}
                        <button onClick={() => setShowPrivacy(true)} className="text-achromatic-400 underline underline-offset-2 hover:text-achromatic-600">개인정보 처리방침</button>
                        에 동의하게 됩니다.
                    </p>
                )}

                {/* Footer */}
                <div className="mt-6 text-center text-[10px] text-achromatic-300 leading-relaxed pb-6">
                    <p>주식회사 젤리펀치 | 대표: 송창환</p>
                    <p>사업자등록번호: 807-81-00467 | 통신판매업: 2022-서울강남-01300</p>
                    <p>전화: 0502-6681-2043</p>
                    <p className="mt-1.5 text-achromatic-400">
                        티팟(TIPOT)은 통신판매중개자이며, 통신판매의 당사자가 아닙니다.
                    </p>
                </div>
            </div>

            {/* Animation keyframes */}
            <style>{`
                @keyframes fadeScale {
                    0% { opacity: 0; transform: scale(0.85); }
                    100% { opacity: 1; transform: scale(1); }
                }
            `}</style>

            {/* Modals */}
            <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
            <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
        </div>
    );
};

export default LoginComponent;